/* ScaiKey login (tenant "scaifm"): authorization code + PKCE in the browser, tokens in sessionStorage.
   Dev builds (VITE_DEV_TOKEN_LOGIN=1) also accept a pasted dev token so the admin works before the client exists. */
import { OAuthClient } from '@scailabs/scaikey';
import { createSignal } from 'solid-js';

const BASE = (import.meta.env.VITE_SCAIKEY_BASE as string | undefined) ?? 'https://scaikey.scailabs.ai';
const TENANT = (import.meta.env.VITE_SCAIKEY_TENANT as string | undefined) ?? 'scaifm';
const CLIENT_ID = import.meta.env.VITE_SCAIKEY_CLIENT_ID as string | undefined;
export const DEV_LOGIN = import.meta.env.VITE_DEV_TOKEN_LOGIN === '1';

const STORE = 'scaifm.admin.session';
const PENDING = 'scaifm.admin.pkce';

type Session = { access_token: string; refresh_token?: string; id_token?: string; expires_at: number };

const oauth = CLIENT_ID ? new OAuthClient({ baseUrl: BASE, clientId: CLIENT_ID, tenantSlug: TENANT }) : null;
const redirectUri = () => `${location.origin}/auth/callback`;

function load(): Session | null {
  try { return JSON.parse(sessionStorage.getItem(STORE) || 'null'); } catch { return null; }
}

const [session, setSessionSignal] = createSignal<Session | null>(load());
export { session };

function setSession(s: Session | null) {
  try { s ? sessionStorage.setItem(STORE, JSON.stringify(s)) : sessionStorage.removeItem(STORE); } catch { /* ignore */ }
  setSessionSignal(s);
}

export const loginConfigured = () => Boolean(oauth);

/** Display-only: who the current token says we are (not verified here; the API verifies). */
export function whoAmI(): { sub?: string; email?: string } {
  const t = session()?.access_token;
  try {
    const b64 = t!.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(b64))));
  } catch { return {}; }
}

export async function login(returnTo = location.pathname + location.search) {
  if (!oauth) throw new Error('ScaiKey client id not configured');
  const { verifier, challenge } = await OAuthClient.generatePkce();
  const state = OAuthClient.generateState();
  sessionStorage.setItem(PENDING, JSON.stringify({ verifier, state, returnTo }));
  location.assign(oauth.getAuthorizationUrl({ redirectUri: redirectUri(), scope: 'openid profile email offline_access',
    state, codeChallenge: challenge }));
}

export async function completeLogin(): Promise<string> {
  if (!oauth) throw new Error('ScaiKey client id not configured');
  const q = new URLSearchParams(location.search);
  const pending = JSON.parse(sessionStorage.getItem(PENDING) || 'null');
  sessionStorage.removeItem(PENDING);
  if (q.get('error')) throw new Error(q.get('error_description') || q.get('error')!);
  if (!pending || q.get('state') !== pending.state) throw new Error('Login state mismatch; please sign in again.');
  const t: any = await oauth.exchangeCode({ code: q.get('code')!, redirectUri: redirectUri(), codeVerifier: pending.verifier });
  setSession({ access_token: t.access_token, refresh_token: t.refresh_token, id_token: t.id_token,
    expires_at: Date.now() + (t.expires_in ?? 3600) * 1000 });
  return pending.returnTo || '/';
}

export function devLogin(token: string) {
  setSession({ access_token: token.trim(), expires_at: Date.now() + 12 * 3600_000 });
}

let refreshing: Promise<string | null> | null = null;

/** A valid access token, refreshed when it is about to expire (refresh tokens rotate on every use). */
export async function accessToken(): Promise<string | null> {
  const s = session();
  if (!s) return null;
  if (s.expires_at - Date.now() > 60_000 || !s.refresh_token || !oauth) return s.access_token;
  refreshing ??= (async () => {
    try {
      const t: any = await oauth.refreshToken({ refreshToken: s.refresh_token! });
      setSession({ access_token: t.access_token, refresh_token: t.refresh_token ?? s.refresh_token,
        id_token: t.id_token ?? s.id_token, expires_at: Date.now() + (t.expires_in ?? 3600) * 1000 });
      return t.access_token as string;
    } catch {
      setSession(null); // refresh family revoked or SSO session ended: sign in again
      return null;
    } finally { refreshing = null; }
  })();
  return refreshing;
}

export async function logout() {
  const s = session();
  setSession(null);
  if (!oauth) { location.assign('/'); return; }
  try {
    // Logout is a browser redirect to the end-session endpoint (the POST routes are not for SPAs).
    const cfg: any = await oauth.getOidcConfiguration();
    const u = new URL(cfg.end_session_endpoint);
    u.searchParams.set('client_id', CLIENT_ID!);
    u.searchParams.set('post_logout_redirect_uri', location.origin + '/');
    if (s?.id_token) u.searchParams.set('id_token_hint', s.id_token);
    location.assign(u.toString());
  } catch {
    location.assign('/');
  }
}
